/* eslint-disable */
import React from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'gatsby';
import styled from '@emotion/styled';

import { rem } from '../../utilities/style';
import { colors } from '../../theme/colors';
import { media } from '../../theme/media';
import { containerStyles } from '../../styles/layout';
import { shadow } from '../../styles/box';
import { headers } from '../../styles/type';

import TextRenderer from '../TextRenderer';
import Link from '../Link';

const Container = styled.div`
  padding: ${rem(32)} 0;
  background-color : ${({ backgroundColor }) => (backgroundColor === 'Blue' ? colors.blue['50'] : colors.white)};

  ${({ theme }) => theme.pageOverrides?.includes('Dark Mode') && `
    background-color: ${colors.blue['800']};
  `}

  ${media('>tiny')} {
    padding: ${rem(56)} 0;
  }
`;

const ContentWrapper = styled.div`
  ${containerStyles.regular}
`;

const Headline = styled.h2`
  ${headers.h3}
  color: ${colors.blue['800']};

  ${({ theme }) => theme.pageOverrides?.includes('Dark Mode') && `
    color: ${colors.blue['100']};
  `}
  margin-bottom: ${rem(40)};
`;

const GroupLinks = styled.div`
`;

const Columns = styled.div`
  ${({ count }) => count > 1 && `
    gap: ${rem(16)};
    display: grid;
    grid-template-columns: 1fr;

    ${media('>tiny')} {
      grid-template-columns: repeat(2, 1fr);
    }

    ${media('>small')} {
      gap: ${rem(30)};
      grid-template-columns: repeat(${count >= 4 ? 4 : 3}, 1fr);
    }

    ${media('>medium')} {
      gap: ${rem(40)};
    }
  `}

  ${({ count }) => count > 3 && `
    gap: ${rem(16)};
  `}

  ${({ count }) => count === 1 && `
    ${GroupLinks} {
      display: grid;
      gap: 0 ${rem(16)};
      grid-template-columns: 1fr;

      ${media('>tiny')} {
        grid-template-columns: repeat(2, 1fr);
      }

      ${media('>small')} {
        grid-template-columns: repeat(4, 1fr);
      }
    }
  `}
`;

const Group = styled.div`

`;

const GroupHeadline = styled.h3`
  ${headers.h5}
  color: ${({ theme }) => (
    theme.pageOverrides?.includes('Dark Mode')
      ? colors.blue['100'] : colors.blue['800']
  )};
  margin-bottom: ${rem(24)};
`;

const ResourceLink = styled(Link)`
  ${headers.h6}
  ${shadow}
  display: block;
  background-color: ${colors.white};
  padding: ${rem(16)} ${rem(20)};
  border-radius: ${rem(10)};
  margin-bottom: ${rem(16)};
  color: ${colors.gray['600']};
  transition: color .2s ease;

  &:hover {
    color: ${colors.gray['800']};
  }

  ${({ theme }) => (theme.pageOverrides?.includes('Dark Mode') && `
    color: ${colors.blue['100']};
    background-color: ${colors.blue['800']};
    border: 1px solid ${colors.blue['700']};
    box-shadow: 0px 8px 16px 0px rgba(0, 0, 0, 0.16), 0px 2px 8px 0px rgba(0, 0, 0, 0.16);

    &:hover {
      color: ${colors.blue['100']};
      background-color: ${colors.blue['700']};
    }
  `)}
`;

const ResourceLinks = ({ headline, resourceGroups, backgroundColor }) => {
  const count = resourceGroups ? resourceGroups.length : 0;

  return (
    <Container backgroundColor={backgroundColor}>
      <ContentWrapper>
        {headline && (
          <Headline>
            <TextRenderer text={headline} />
          </Headline>
        )}
        <Columns count={count}>
          {resourceGroups?.map((group, index) => (
            <Group
              // eslint-disable-next-line react/no-array-index-key
              key={index}
            >
              {group.title && (
                <GroupHeadline>
                  <TextRenderer text={group.title} />
                </GroupHeadline>
              )}
              {group.resourceGroupLinks && (
                <GroupLinks>
                  {group.resourceGroupLinks.map((link, linkIndex) => (
                    <ResourceLink
                      // eslint-disable-next-line react/no-array-index-key
                      key={linkIndex}
                      to={link.url}
                    >
                      <TextRenderer text={link.title} />
                    </ResourceLink>
                  ))}
                </GroupLinks>
              )}
            </Group>
          ))}
        </Columns>
      </ContentWrapper>
    </Container>
  );
};

ResourceLinks.propTypes = {
  headline: PropTypes.string,
  resourceGroups: PropTypes.arrayOf(PropTypes.shape({
    title: PropTypes.string,
    resourceGroupLinks: PropTypes.arrayOf(PropTypes.shape({
      title: PropTypes.string,
      url: PropTypes.string,
    })),
  })),
  backgroundColor: PropTypes.oneOf(['Blue', 'White'])
};

export const query = graphql`
  fragment ResourceLinksFragment on ContentfulResourceLinks {
    id
    __typename
    headline
    resourceGroups {
      title
      resourceGroupLinks {
        title
        url
      }
    }
    backgroundColor
    personalizations {
      ...PersonalizationFragment
    }
  }
`;

export default ResourceLinks;
